import { ReactComponent as SwitchTeamsIcon } from 'components/icons/switch-home-away-icon.svg';
import { ServiceTypes } from 'constants/ServiceTypes';
import { SessionMode } from 'enums/SessionMode';
import { SessionType } from 'enums/SessionType';
import { History } from 'history';
import type { ICustomStrikeZone } from 'interfaces/session/ICustomStrikeZone';
import { ISessionMode } from 'interfaces/session/ISessionMode';
import { IRosterResponse } from 'interfaces/team/IRosterResponse';
import { ITeam } from 'interfaces/team/ITeam';
import { observer } from 'mobx-react-lite';
import { LineupManager } from 'models/setLineup/LineupManager';
import React, { useEffect, useState } from 'react';
import { FeatureConfigService } from 'services/FeatureConfigService';
import { useInjection } from 'services/ioc/ioc.react';
import RulesetService from 'services/RulesetService';
import { ContextStore } from 'stores/ContextStore';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { LineupStore } from 'stores/LineupStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { TeamStore } from 'stores/TeamStore';
import { UiStore } from 'stores/UiStore';
import { LogService } from '../../services/data-services/LogService';
import SetLineupBottomBar from './components/SetLineupBottomBar/SetLineupBottomBar';
import TeamRoster from './components/TeamRoster/TeamRoster';
import { LineupSelectors, SetLineupWrapper, StyledSwitchButton, Title, TitleWrapper } from './SetLineupStyles';

interface IProps {
  history: History;
}

const SetLineup: React.FC<IProps> = observer((props) => {
  const taggingStore = useInjection<TaggingStore>(ServiceTypes.TaggingStore);
  const teamStore = useInjection<TeamStore>(ServiceTypes.TeamStore);
  const lineupStore = useInjection<LineupStore>(ServiceTypes.LineupStore);
  const sessionStore = useInjection<SessionStore>(ServiceTypes.SessionStore);
  const contextualDataStore = useInjection<ContextualDataStore>(ServiceTypes.ContextualDataStore);
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const contextStore = useInjection<ContextStore>(ServiceTypes.ContextStore);
  const logService = useInjection<LogService>(ServiceTypes.LogService);
  const { history } = props;
  const rulesetService = useInjection<RulesetService>(ServiceTypes.RulesetService);
  const [isHomeTeamLoading, setIsHomeTeamLoading] = useState(false);
  const [isAwayTeamLoading, setIsAwayTeamLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [homeTeamManager, setHomeTeamManager] = useState<LineupManager>();
  const [awayTeamManager, setAwayTeamManager] = useState<LineupManager>();
  const featureConfigService = useInjection<FeatureConfigService>(ServiceTypes.FeatureConfigService);
  // const [selectedUmpire, setSelectedUmpire] = useState<IUmpire>();
  const notificationStore = useInjection<NotificationStore>(ServiceTypes.NotificationStore);

  const changeSessionMode = async (): Promise<void> => {
    const sessionData: ISessionMode = {
      sessionMode: SessionMode.SessionStarted,
      sessionType: sessionStore.initialMetadata?.externalSessionId ? SessionType.Standard : SessionType.Adhoc,
      systemId: contextStore.activeField!.systemId,
      time: '', // will be set in store anyway
      strikeZone: lineupStore.strikeZone,
      isVideoCaptureEnabled: lineupStore.isVideoCaptureEnabled,
      isPlayerTrackingEnabled: lineupStore.isPlayerTrackingEnabled,
      issuer: 'TaggingApp',
      ...sessionStore.initialMetadata!,
    };

    if (featureConfigService.getConfig().isRoboUmpEnabled || lineupStore.strikeZone?.name === 'NCAA') {
      sessionData.strikeZone = lineupStore.strikeZone;
    } else {
      sessionData.strikeZone = undefined;
    }

    const isSuccess = await sessionStore.setCurrentSessionMode(sessionData);
    if (!isSuccess) {
      notificationStore.addNotification({
        id: 'session-mode-error',
        message:
          'There was an error with session mode change. Please go back to main screen and start over. Remember, that running the app in multiple tabs at the same time is not supported.',
        variant: 'error',
        removable: true,
        onClose: () => {
          notificationStore.removeNotification('session-mode-error');
        },
      });
    }
  };

  const createLineupManager = (team: ITeam, roster: IRosterResponse | undefined): LineupManager => {
    const activeStrikeZone =
      lineupStore.overridedStrikeZone?.activeStrikeZone ||
      sessionStore.currentSessionMetadata.strikeZone?.customDefinition?.activeStrikeZone ||
      sessionStore.currentSessionMetadata.strikeZone?.activeStrikeZone;

    const manager = new LineupManager(
      team,
      lineupStore.ruleset,
      roster?.players || [],
      activeStrikeZone,
      rulesetService.getPlayerLimit(lineupStore.ruleset),
      undefined,
      sessionStore.currentSessionMode.privateSession,
      sessionStore.currentSessionMode.privateSession
    );

    manager.autoFillLineUp(roster?.lineup || []);
    return manager;
  };

  const onHomeTeamSelected = async (team?: ITeam): Promise<void> => {
    if (team) {
      setIsHomeTeamLoading(true);
      const roster = await teamStore.fetchPlayers(team.id);
      const newHomeTeamManager = createLineupManager(team, roster);

      setHomeTeamManager(newHomeTeamManager);
      setIsHomeTeamLoading(false);
    } else {
      setHomeTeamManager(undefined);
    }
  };

  const onAwayTeamSelected = async (team?: ITeam): Promise<void> => {
    if (team) {
      setIsAwayTeamLoading(true);
      const roster = await teamStore.fetchPlayers(team.id);
      const newAwayTeamManager = createLineupManager(team, roster);

      setAwayTeamManager(newAwayTeamManager);
      setIsAwayTeamLoading(false);
    } else {
      setAwayTeamManager(undefined);
    }
  };

  const isStartTaggingDisabled = (): boolean => {
    return !awayTeamManager?.isValidated || !homeTeamManager?.isValidated || !sessionStore.isGameInProgress;
  };

  const switchTeams = async (): Promise<void> => {
    const homeManager = homeTeamManager;
    setHomeTeamManager(awayTeamManager);
    setAwayTeamManager(homeManager);
  };

  const startTagging = async (): Promise<void> => {
    if (!homeTeamManager || !awayTeamManager) {
      return;
    }

    let customStrikeZone: ICustomStrikeZone | null = null;

    if (featureConfigService.getConfig().isRoboUmpEnabled && sessionStore.currentSessionMode.privateSession) {
      customStrikeZone = {
        definition: await lineupStore.getOverridedStrikeZoneForSessionMetadata(),
        locations: lineupStore.getPlaneLocationsForSessionMetadata(),
      };
    }

    await taggingStore.initializeFromLineup(
      homeTeamManager,
      awayTeamManager,
      lineupStore.gameLevel,
      lineupStore.league,
      lineupStore.ruleset,
      customStrikeZone,
      lineupStore.owningOrganization.id
      // selectedUmpire
    );

    if (sessionStore.currentSessionMode.isOperatorlessTaggingEnabled) {
      history.push('/');
    } else {
      history.push('/tagging');
    }
  };

  const initialize = async (): Promise<void> => {
    setIsInitializing(true);
    const teamsPromise = teamStore.fetchTeams();
    if (!sessionStore.isSessionStarted && sessionStore.initialMetadata) {
      await changeSessionMode();
    }
    if (sessionStore.currentSessionMode.gameScheduleReference) {
      await teamsPromise;
      const gsr = sessionStore.currentSessionMode.gameScheduleReference;
      const away = teamStore.getTeamById(gsr.awayTeam.id);
      const home = teamStore.getTeamById(gsr.homeTeam.id);
      const homePromise = onHomeTeamSelected(home);
      const awayPromise = onAwayTeamSelected(away);
      await Promise.all([homePromise, awayPromise]);
    } else {
      if (sessionStore.currentSessionMetadata.awayTeam && sessionStore.currentSessionMetadata.homeTeam) {
        const away = sessionStore.currentSessionMetadata.awayTeam;
        const home = sessionStore.currentSessionMetadata.homeTeam;
        const homePromise = onHomeTeamSelected(home);
        const awayPromise = onAwayTeamSelected(away);
        await Promise.all([homePromise, awayPromise]);
      }
    }
    setIsInitializing(false);
  };

  useEffect(() => {
    // Needs to be on every page, so latest sync is set when user changes page
    contextualDataStore.setLatestSync();
    logService.logInfo('Open page: SetLineup');
    // This check prevents the user from accessing the page any other way than through the correct flow, since isInitialMetaDataConfirmed is set from the GameDialog
    if (!sessionStore.initialMetadata) {
      history.replace('/');
    }

    uiStore.setIsSettingLineup(true);
    return (): void => {
      sessionStore.setInitialMetadata(undefined);
      lineupStore.resetSessionMetaDataValues();
      uiStore.setIsSettingLineup(false);
    };
  }, []);

  useEffect(() => {
    initialize();
  }, []);

  const getRequiredPositionCount = () => {
    let count = 0;

    if (homeTeamManager) {
      count += homeTeamManager.getRequiredPositions().length;
    }

    if (awayTeamManager) {
      count += awayTeamManager.getRequiredPositions().length;
    }

    return count;
  };

  const isAdHoc = (): boolean => {
    return (
      sessionStore.currentSessionMode.sessionType === SessionType.Adhoc &&
      sessionStore.currentSessionMode.sessionMode === SessionMode.SessionStarted
    );
  };

  return (
    <SetLineupWrapper>
      {/* <UmpireSelector selectedUmpire={selectedUmpire} setSelectedUmpire={setSelectedUmpire} />/ */}
      <TitleWrapper>
        <Title>Create line up</Title>
        {isAdHoc() && (
          <StyledSwitchButton intent='tertiary' label='Switch home/away' onClick={switchTeams} startIcon={<SwitchTeamsIcon />} />
        )}
      </TitleWrapper>
      <LineupSelectors>
        <TeamRoster
          isLoading={teamStore.isLoading || isAwayTeamLoading || isInitializing}
          onTeamSelected={onAwayTeamSelected}
          teamManager={awayTeamManager}
          oppositeTeamManager={homeTeamManager}
          className='left-side'
          title='Away Team'
          placeholder='Click to search for a team name'
          data-test='search-away'
          size='sm'
          placeholderText='No away team has been added yet'
        />
        <TeamRoster
          isLoading={teamStore.isLoading || isHomeTeamLoading || isInitializing}
          onTeamSelected={onHomeTeamSelected}
          teamManager={homeTeamManager}
          oppositeTeamManager={awayTeamManager}
          className='right-side'
          title='Home Team'
          placeholder='Click to search for a team name'
          data-test='search-home'
          size='sm'
          placeholderText='No home team has been added yet'
        />
      </LineupSelectors>
      <SetLineupBottomBar
        sessionMode={sessionStore.currentSessionMode}
        onClickStartTaggingCallback={startTagging}
        startTaggingDisabled={isStartTaggingDisabled()}
        isLoading={taggingStore.isLoading}
        onClickSwapTeamsCallback={switchTeams}
        homeTeamManager={homeTeamManager && homeTeamManager}
        awayTeamManager={awayTeamManager && awayTeamManager}
        requiredPositionsCount={getRequiredPositionCount()}
        homePositions={homeTeamManager?.getFilledPositions()}
        awayPositions={awayTeamManager?.getFilledPositions()}
      />
    </SetLineupWrapper>
  );
});

export default SetLineup;
